import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'; 
import { AuthProvider, useAuth } from './context/AuthContext';
import SignUp from './components/SignUp';
import SignIn from './components/SignIn';
import Info from './components/Info'; 
import OutletSelection from './components/OutletSelection';
import AccountSwitch from './components/AccountSwitch';

import ForgotPassword from './components/ForgotPassword';
import Support from './components/Support/Support';
import ViewSupport from './components/Support/ViewSupport';
import PrivateRoute from './Route/PrivateRoute';
import Sidebar from './components/Sidebar';
import AccountDelete from './components/AccountDelete';

// Dashboard and feature pages
import Dashboard from './pages/Dashboard/Dashboard';
import ProductManager from './pages/Item/Product';
import ExpenseTracker from './pages/Expense/ExpenseTracker';
import NewBill from './pages/Sales/NewBill';
import HoldBill from './pages/Sales/HoldBill';
import MenuSelection from './pages/Sales/MenuSelection';
import ExistingHoldItemsSection from './pages/Sales/ExistingHoldItemsSection';
import PrintKOT from './pages/Sales/PrintKOT';
import KOT from './pages/KOT/KOT';

import TableManager from './pages/ManageTable/ManageTable';

import ExpenseReport from './pages/Report/ExpenseReport';
import SalesReport from './pages/Report/SalesReport';
import SubscriptionPage from './context/SubscriptionPage';
import ViewSubscriptionInfo from './context/ViewSubscriptionInfo';

import Settings from './pages/Settings/Settings';
import BillHistory from './pages/BillHistory/BillHistory';
import BillDetails from './pages/BillHistory/BillDetails';

import PrintBill from './pages/GenerateBill/PrintBill';

// Landing Page components
import Navbar from './components/Navbar/Navbar';
import Home from './Page/Home/Home';
import Footer from './components/Footer/Footer';
import About from  './Page/About/About';
import Gallery from './Page/Gallery/Gallery';
import ContactUs from './Page/Contact/ContactUs';
import OurMenus from './Page/Menus/OurMenus';
import MenuItem from './Page/MenuItem/MenuItem';

const App = () => {
  return (
    <AuthProvider>
      <Router>
        <Main />
      </Router>
    </AuthProvider>
  );
};

const Main = () => {
  const location = useLocation();
  const { isLoading, isAuthenticated } = useAuth();
  
  // Routes that should have Navbar and Footer
  const navbarFooterRoutes = [
    "/", 
    "/about-us", 
    "/gallery", 
    "/contact", 
    "/our-menus",
    "/menu-item"
  ];
  
  // Check if current path should have Navbar and Footer
  const shouldShowNavbarFooter = navbarFooterRoutes.some(route => 
    location.pathname === route || 
    (route === "/menu-item" && location.pathname.startsWith("/menu-item/")) ||
    location.pathname.startsWith("/menu/")
  );
  
  // Public routes where sidebar should be hidden - made case insensitive
  const publicRoutes = [
    "/signup", 
    "/signin",
    "/outlet-selection", 
    "/forgot-password",
    "/info",
    "/deleteappaccount"
  ];
  
  // Remove support from publicRoutes and handle it separately
  
  // Check if current path is a public route (where sidebar is hidden) - case insensitive check
  const isPublicRoute = publicRoutes.some(route => 
    location.pathname.toLowerCase() === route.toLowerCase()
  ) || shouldShowNavbarFooter;
  
  // Determine if support is being accessed publicly
  const isPublicSupport = location.pathname.toLowerCase() === "/support" && !location.search.includes("auth=true");
  
  // Only show sidebar on authenticated routes and when we're not loading
  const shouldShowSidebar = !isLoading && isAuthenticated && !isPublicRoute && !isPublicSupport;

  // Show loading indicator while authentication state is being loaded
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {shouldShowNavbarFooter && <Navbar />}
      
      <div className="flex flex-1">
        {shouldShowSidebar && <Sidebar />}
        <div className={`flex-1 min-h-screen ${!shouldShowSidebar ? 'w-full' : ''}`}>
          <Routes>
            {/* Public landing page routes with Navbar and Footer */}
            <Route path="/" element={<Home />} />
            <Route path="/about-us" element={<About />} />
            <Route path="/gallery" element={<Gallery
              accessToken="IGQWROaEdMTW1CbHQybVBwYlk1eE5TWHRRQngwZAWNDa2ZAUWTFuWTMzb213NWR0WFZANcEJwWnI2d1ZArYjliazRyQlgxd3ZAES3hWZAlZAvbWxlRkswVk5ldzdhZAHFrcUs4MzVRazF3NW5GZAUtMVVhGbkFuOHlxRHlvaE0ZD"
              limit={21}
            />} />
            <Route path="/contact" element={<ContactUs />} /> 
            <Route path="/our-menus" element={<OurMenus />} />
            <Route path="/menu/*" element={<OurMenus />} />
            <Route path="/menu-item/:id" element={<MenuItem />} />
            
            {/* Authentication routes */}
            <Route path="/signup" element={<SignUp />} />
            <Route path="/signin" element={!isAuthenticated ? <SignIn /> : <Navigate to="/dashboard" />} />
            <Route path="/outlet-selection" element={<OutletSelection />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/info" element={<Info />} />
            
            {/* Support route that can be accessed publicly or privately */}
            <Route path="/support" element={
              isAuthenticated ? 
                <Support /> :
                <Support />
            } />
            
            {/* Private routes */}
            <Route path="/View-Support" element={<PrivateRoute><ViewSupport /></PrivateRoute>} />
            <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/product" element={<PrivateRoute><ProductManager /></PrivateRoute>} />
            <Route path="/Expense" element={<PrivateRoute><ExpenseTracker /></PrivateRoute>} />
            <Route path="/new-bill" element={<PrivateRoute><NewBill /></PrivateRoute>} />
            <Route path="/hold-bill" element={<PrivateRoute><HoldBill /></PrivateRoute>} />
            <Route path="/menu-selection" element={<PrivateRoute><MenuSelection /></PrivateRoute>} />
            <Route path="/existing-hold-items" element={<PrivateRoute><ExistingHoldItemsSection /></PrivateRoute>} />
            <Route path="/print-kot/:tableId" element={<PrivateRoute><PrintKOT /></PrivateRoute>} />
            <Route path="/account-switch" element={<PrivateRoute><AccountSwitch /></PrivateRoute>} />

            <Route path="/kot" element={<PrivateRoute><KOT /></PrivateRoute>} />
            <Route path="/table-manager" element={<PrivateRoute><TableManager /></PrivateRoute>} />
            <Route path='/expense-report'element={<PrivateRoute><ExpenseReport /></PrivateRoute>} />
            <Route path='/sales-report'element={<PrivateRoute><SalesReport /></PrivateRoute>} />
            <Route path="/subscription" element={<PrivateRoute><SubscriptionPage /></PrivateRoute>} />
            <Route path="/ViewSubscriptionInfo" element={<PrivateRoute><ViewSubscriptionInfo /></PrivateRoute>} />
            <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
            <Route path="/AccountDelete" element={<PrivateRoute><AccountDelete /></PrivateRoute>} />
            <Route path="/print-bill/:billId" element={<PrivateRoute><PrintBill /></PrivateRoute>} />
            <Route path="/bill-history" element={<PrivateRoute><BillHistory /></PrivateRoute>} />
            <Route path="/billdetails/:billId" element={<PrivateRoute><BillDetails /></PrivateRoute>} />
            
            <Route path="*" element={isAuthenticated ? <Navigate to="/dashboard" /> : <Navigate to="/signin" />} />
          </Routes>
        </div>
      </div>
      
      {shouldShowNavbarFooter && <Footer />}
    </div>
  );
};

export default App;